import React, { useState, useEffect, useRef } from "react";
import { database } from "../../firebase";
import { ref, set, serverTimestamp } from "firebase/database";
import { Html5Qrcode } from "html5-qrcode";
import "./Scanner.css";


const ErrorMessage = ({ message }) => {
  if (!message) return null;

  return (
    <div
      style={{
        color: "red",
        textAlign: "center",
        marginBottom: "10px",
        fontWeight: "bold",
      }}
    >
      {message}
    </div>
  );
};


const Scanner = ({ user, onStopScan, setUniqueKey, setUserID }) => {
  const [message, setMessage] = useState("");
  const qrReaderRef = useRef(null);
  const isProcessingRef = useRef(false); // 👈 Flag to prevent multiple processing

  const getLocation = () =>
    new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(resolve, reject, {
        enableHighAccuracy: true,
        timeout: 10000,
      });
    });

  const getLongitudeAndLatitude = async () => {
    const position = await getLocation();
    const latitude = position.coords.latitude;
    const longitude = position.coords.longitude;
    return { latitude, longitude };
  };

  const handleDecodedText = async (decodedText) => {
    // 👇 Skip if already processing a scan
    if (isProcessingRef.current) return;

    isProcessingRef.current = true; // 👈 Set flag to block re-entry

    try {
      const { latitude, longitude } = await getLongitudeAndLatitude();

      if (qrReaderRef.current) {
        await qrReaderRef.current.stop();
        qrReaderRef.current = null;
      }

      // Ensure `user.email` and `decodedText` are defined before performing `.replace()` to avoid runtime errors
      const userPath = user?.email ? user.email.replace(".", "-") : "unknown-user";
      const sanitizedData = decodedText ? decodedText.replace(/[^a-zA-Z0-9_-]/g, "-") : "invalid-data";
      const timestamp = Date.now();
      const uniqueKey = `${sanitizedData}(${timestamp})`;
      const scanRef = ref(database, `data/scans/${userPath}/${uniqueKey}`);

      const scanData = {
        coordinates: { x: latitude, y: longitude },
        qrcodeData: decodedText,
        timestamp: serverTimestamp(),
        completeAddress: null,
      };

      await set(scanRef, scanData);
      console.log("Data saved successfully");

      setUniqueKey(uniqueKey);
      setUserID(userPath);
      onStopScan();
    } catch (locationError) {
      console.error("Location error:", locationError);
      const code = locationError.code;
      const messages = {
        1: "Location permission denied. Please allow location access.",
        2: "Location unavailable. Please check your GPS settings.",
        3: "Location request timed out. Try again.",
      };
      setMessage(messages[code] || "Failed to retrieve location.");

      if (qrReaderRef.current) {
        await qrReaderRef.current.stop();
        qrReaderRef.current = null;
      }
    } finally {
      isProcessingRef.current = false; // 👈 Reset flag (optional if you never want to re-enable scan in same session)
    }
  };

  useEffect(() => {
    const startScanning = async () => {
      try {
        if (!navigator.geolocation) {
          setMessage("Geolocation is not supported by this browser.");
          return;
        }

        if (qrReaderRef.current) {
          await qrReaderRef.current.stop();
          qrReaderRef.current = null;
        }

        const qrReader = new Html5Qrcode("reader");
        qrReaderRef.current = qrReader;

        await qrReader.start(
          { facingMode: "environment" },
          { fps: 10, qrbox: { width: 400, height: 400 } },
          handleDecodedText,
          (scanError) => {
            console.log("QR Scan Error:", scanError);
          }
        );

        console.log("Scanning started...");
      } catch (err) {
        console.error("Scanner init error:", err.message);
        setMessage("Unable to start scanner. Check camera and location permissions.");
      }
    };

    startScanning();

    return () => {
      if (qrReaderRef.current) {
        qrReaderRef.current
          .stop()
          .then(() => {
            qrReaderRef.current = null;
          })
          .catch((err) => console.error("Error stopping scanner:", err));
      }
    };
  }, [user, onStopScan, setUniqueKey, setUserID]);

  return (
    <div>
      <h2 className="scanner-heading">QR Code Scanner</h2>
      <ErrorMessage message={message} />
      <div id="reader" className="qr-reader"></div>
    </div>
  );
};

export default Scanner;
